import React, {
    useEffect,
    useState,
} from "react";

import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

import {
    Link,
    useSearchParams,
} from "react-router-dom";

import Layout from "../components/layoutBlack";
import Breadcrumbs from "../components/breadcrumbs";

import { config } from "../utils/constants";

const REACT_APP_API_URL =
    config.url.REACT_APP_API_URL;


const getErrorMessage = (error) => {
    const responseData =
        error.response?.data;

    if (!responseData) {
        return (
            "We could not connect to the server. " +
            "Please check your connection and try again."
        );
    }

    if (typeof responseData === "string") {
        return responseData;
    }

    const detail = responseData.detail;

    if (Array.isArray(detail)) {
        return detail.join(" ");
    }

    if (typeof detail === "string") {
        return detail;
    }

    return (
        "This password reset link is invalid or has expired."
    );
};


const ResetPassword = () => {
    const [
        searchParams,
    ] = useSearchParams();

    const [
        status,
        setStatus,
    ] = useState("idle");

    const [
        message,
        setMessage,
    ] = useState("");

    const uid =
        searchParams.get("uid")?.trim() ||
        "";

    const token =
        searchParams.get("token")?.trim() ||
        "";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const formik = useFormik({
        initialValues: {
            newPassword: "",
            confirmPassword: "",
        },

        validationSchema: Yup.object({
            newPassword: Yup.string()
                .min(
                    8,
                    "Password must be at least 8 characters"
                )
                .required(
                    "New password is required"
                ),

            confirmPassword: Yup.string()
                .oneOf(
                    [Yup.ref("newPassword")],
                    "Passwords must match"
                )
                .required(
                    "Please confirm your new password"
                ),
        }),

        onSubmit: async (
            values,
            {
                setSubmitting,
            }
        ) => {
            setMessage("");

            if (!uid || !token) {
                setStatus("error");

                setMessage(
                    "This password reset link is missing " +
                    "required information."
                );

                setSubmitting(false);
                return;
            }

            try {
                const response =
                    await axios.post(
                        `${REACT_APP_API_URL}/auth/password-reset-confirm/`,
                        {
                            uid,
                            token,
                            new_password:
                                values.newPassword,
                            confirm_password:
                                values.confirmPassword,
                        }
                    );

                setStatus("success");

                setMessage(
                    response.data?.detail ||
                        (
                            "Your password has been reset " +
                            "successfully. You can now sign in " +
                            "with your new password."
                        )
                );
            } catch (error) {
                setStatus("error");
                setMessage(getErrorMessage(error));
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div>
            <Layout>
                <div className="container p-t-80 p-b-10">
                    <Breadcrumbs />
                </div>

                <section className="bg0 p-t-30 p-b-20">
                    <div className="container">
                        <div className="txt-center">
                            <h2 className="ltext-105 cl2">
                                Reset Your Password
                            </h2>
                        </div>
                    </div>
                </section>

                <section className="full-page-section bg0 p-t-30 p-b-125">
                    <div className="container">
                        <div
                            className="m-lr-auto"
                            style={{
                                maxWidth: "560px",
                            }}
                        >
                            <div className="bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg">
                                {status === "success" ? (
                                    <div
                                        className="txt-center"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <h4 className="mtext-105 cl2 p-b-15">
                                            Password Reset
                                        </h4>

                                        <div
                                            className="alert alert-success m-b-25"
                                            role="alert"
                                        >
                                            {message}
                                        </div>

                                        <Link
                                            to="/login"
                                            className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04"
                                        >
                                            Sign In
                                        </Link>
                                    </div>
                                ) : (
                                    <form
                                        method="post"
                                        onSubmit={
                                            formik.handleSubmit
                                        }
                                        noValidate
                                    >
                                        {status === "error" ? (
                                            <div
                                                className="alert alert-danger m-b-25"
                                                role="alert"
                                                aria-live="polite"
                                            >
                                                {message}

                                                <div className="p-t-10">
                                                    <Link
                                                        to="/request-password-reset"
                                                        className="cl1 hov-cl1 trans-04"
                                                    >
                                                        Request a
                                                        new reset
                                                        link
                                                    </Link>
                                                </div>
                                            </div>
                                        ) : null}

                                        <div className="m-b-20">
                                            <label
                                                htmlFor="newPassword"
                                                className="stext-102 cl3 p-b-8"
                                                style={{
                                                    display:
                                                        "block",
                                                }}
                                            >
                                                New Password
                                            </label>

                                            <div className="bor8">
                                                <input
                                                    id="newPassword"
                                                    className="stext-111 cl2 plh3 size-116 p-l-30 p-r-30"
                                                    type="password"
                                                    name="newPassword"
                                                    placeholder="New password"
                                                    autoComplete="new-password"
                                                    value={
                                                        formik
                                                            .values
                                                            .newPassword
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={
                                                        formik.handleBlur
                                                    }
                                                />
                                            </div>

                                            {formik.touched
                                                .newPassword &&
                                            formik.errors
                                                .newPassword ? (
                                                <div className="text-danger mt-1 text-sm p-t-5">
                                                    {
                                                        formik
                                                            .errors
                                                            .newPassword
                                                    }
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="m-b-20">
                                            <label
                                                htmlFor="confirmPassword"
                                                className="stext-102 cl3 p-b-8"
                                                style={{
                                                    display:
                                                        "block",
                                                }}
                                            >
                                                Confirm New
                                                Password
                                            </label>

                                            <div className="bor8">
                                                <input
                                                    id="confirmPassword"
                                                    className="stext-111 cl2 plh3 size-116 p-l-30 p-r-30"
                                                    type="password"
                                                    name="confirmPassword"
                                                    placeholder="Confirm new password"
                                                    autoComplete="new-password"
                                                    value={
                                                        formik
                                                            .values
                                                            .confirmPassword
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={
                                                        formik.handleBlur
                                                    }
                                                />
                                            </div>

                                            {formik.touched
                                                .confirmPassword &&
                                            formik.errors
                                                .confirmPassword ? (
                                                <div className="text-danger mt-1 text-sm p-t-5">
                                                    {
                                                        formik
                                                            .errors
                                                            .confirmPassword
                                                    }
                                                </div>
                                            ) : null}
                                        </div>

                                        <button
                                            className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer"
                                            type="submit"
                                            disabled={
                                                formik.isSubmitting
                                            }
                                        >
                                            {formik.isSubmitting
                                                ? "Resetting..."
                                                : "Reset Password"}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </div>
    );
};


export default ResetPassword;
